import { useEffect, useState, useMemo} from "react";
import { Container, Row, Col, Card, Spinner, Modal, Button } from "react-bootstrap";
import { HOST, API_URL, AVATAR_URL } from "../constants";
import UserImage from "../assets/user.png";
import axios from "axios";
import DoulaNotesNavbar from "../components/DoulaNotesNavbar";

const getAvatarUrl = (image) => {
    if (image) {
        return `${HOST}/${AVATAR_URL}/${image}`;
    }
    return UserImage;
};

// Normalize: lowercase first, then title-case long values or uppercase short ones (abbreviations)
// "NY" / "ny" / "Ny" → "NY", "new york" / "NEW YORK" → "New York"
const normalize = (str) => {
    const lowered = str.trim().toLowerCase();
    if (lowered.length <= 2) return lowered.toUpperCase();
    return lowered.replace(/\b\w/g, (c) => c.toUpperCase());
};

// Reject actual nulls, empty strings, and the literal string "null"
const isValid = (val) => val && val.trim().toLowerCase() !== "null";

const DoulaDirectory = () => {
    const [doulas, setDoulas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDoula, setSelectedDoula] = useState(null);
    const [selectedState, setSelectedState] = useState("");
    const [selectedCounty, setSelectedCounty] = useState("");
    const [selectedLanguage, setSelectedLanguage] = useState("");
    const [showModal, setShowModal] = useState(false);

    const handleOpenModal = (doula) => {
        setSelectedDoula(doula);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedDoula(null);
    };

    const clearFilters = () => {
        setSelectedState("");
        setSelectedCounty("");
        setSelectedLanguage("");
    };

    const hasActiveFilters = selectedState || selectedCounty || selectedLanguage;

    useEffect(() => {
        axios
            .get(`${HOST}/${API_URL}/doulas/directory`)
            .then((res) => {
                setDoulas(res.data);
            })
            .catch((err) => {
                console.error("Failed to fetch doulas:", err);
            })
            .finally(() => setLoading(false));
    }, []);

    const uniqueStates = useMemo(() => {
        return [...new Set(
            doulas.map((d) => d.state).filter(isValid).map(normalize)
        )].sort();
    }, [doulas]);

    const uniqueCounties = useMemo(() => {
        return [...new Set(
            doulas.map((d) => d.county).filter(isValid).map(normalize)
        )].sort();
    }, [doulas]);

    const uniqueLanguages = useMemo(() => {
        const langs = new Set();
        doulas.forEach((d) => {
            if (isValid(d.language)) {
                d.language.split(",").forEach((l) => {
                    if (isValid(l)) langs.add(normalize(l));
                });
            }
        });
        return [...langs].sort();
    }, [doulas]);

    const filteredDoulas = useMemo(() => {
        return doulas.filter((doula) => {
            const matchesState = !selectedState ||
                (isValid(doula.state) && normalize(doula.state) === selectedState);
            const matchesCounty = !selectedCounty ||
                (isValid(doula.county) && normalize(doula.county) === selectedCounty);
            const matchesLanguage = !selectedLanguage || (
                isValid(doula.language) &&
                doula.language.split(",").filter(isValid).map(normalize).includes(selectedLanguage)
            );
            return matchesState && matchesCounty && matchesLanguage;
        });
    }, [doulas, selectedState, selectedCounty, selectedLanguage]);

    if (loading) {
        return (
            <div style={{ backgroundColor: "#dec5ae", minHeight: "100vh" }}>
                <DoulaNotesNavbar />
                <Container className="text-center mt-5">
                    <Spinner animation="border" style={{ color: "#4a3b2f" }} />
                </Container>
            </div>
        );
    }

    return (
        <>
        <div style={{ backgroundColor: "#dec5ae", minHeight: "100vh", paddingBottom: "2rem" }}>
            <DoulaNotesNavbar />
            <Container className="mt-4">
                <h2 className="mb-1 text-center" style={{ fontWeight: "700", color: "#4a3b2f" }}>
                    DoulaNotes Directory
                </h2>
                <p className="text-center mb-4" style={{ color: "#7a6355", fontSize: "0.95rem" }}>
                    {filteredDoulas.length} doula{filteredDoulas.length !== 1 ? "s" : ""} found
                </p>

                {/* Filter Panel */}
                <div
                    style={{
                        backgroundColor: "#f5ede3",
                        borderRadius: "12px",
                        padding: "20px 24px",
                        marginBottom: "28px",
                        boxShadow: "0 2px 8px rgba(74,59,47,0.08)",
                        border: "1px solid #e0cfc0",
                    }}
                >
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <span style={{ fontWeight: "600", color: "#4a3b2f", fontSize: "0.95rem", letterSpacing: "0.03em" }}>
                            Filter Doulas
                        </span>
                        {hasActiveFilters && (
                            <button
                                onClick={clearFilters}
                                style={{
                                    background: "none",
                                    border: "none",
                                    color: "#9b6b47",
                                    fontSize: "0.85rem",
                                    cursor: "pointer",
                                    textDecoration: "underline",
                                    padding: 0,
                                }}
                            >
                                Clear all
                            </button>
                        )}
                    </div>
                    <Row className="g-3">
                        <Col xs={12} sm={6} lg={4}>
                            <label style={{ fontSize: "0.78rem", fontWeight: "600", color: "#7a6355", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "6px", display: "block" }}>
                                State
                            </label>
                            <select
                                className="form-select form-select-sm"
                                value={selectedState}
                                onChange={(e) => setSelectedState(e.target.value)}
                                style={{ borderColor: "#d4bfad", borderRadius: "8px", fontSize: "0.9rem" }}
                            >
                                <option value="">All states</option>
                                {uniqueStates.map((state) => (
                                    <option key={state} value={state}>{state}</option>
                                ))}
                            </select>
                        </Col>
                        <Col xs={12} sm={6} lg={4}>
                            <label style={{ fontSize: "0.78rem", fontWeight: "600", color: "#7a6355", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "6px", display: "block" }}>
                                County
                            </label>
                            <select
                                className="form-select form-select-sm"
                                value={selectedCounty}
                                onChange={(e) => setSelectedCounty(e.target.value)}
                                style={{ borderColor: "#d4bfad", borderRadius: "8px", fontSize: "0.9rem" }}
                            >
                                <option value="">All counties</option>
                                {uniqueCounties.map((county) => (
                                    <option key={county} value={county}>{county}</option>
                                ))}
                            </select>
                        </Col>
                        <Col xs={12} sm={6} lg={4}>
                            <label style={{ fontSize: "0.78rem", fontWeight: "600", color: "#7a6355", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "6px", display: "block" }}>
                                Language
                            </label>
                            <select
                                className="form-select form-select-sm"
                                value={selectedLanguage}
                                onChange={(e) => setSelectedLanguage(e.target.value)}
                                style={{ borderColor: "#d4bfad", borderRadius: "8px", fontSize: "0.9rem" }}
                            >
                                <option value="">All languages</option>
                                {uniqueLanguages.map((lang) => (
                                    <option key={lang} value={lang}>{lang}</option>
                                ))}
                            </select>
                        </Col>
                    </Row>
                </div>

                {/* Doula Cards */}
                {filteredDoulas.length === 0 ? (
                    <div className="text-center py-5" style={{ color: "#7a6355" }}>
                        <p style={{ fontSize: "1.1rem" }}>No doulas match your filters.</p>
                        <button
                            onClick={clearFilters}
                            style={{
                                background: "none",
                                border: "1px solid #9b6b47",
                                color: "#9b6b47",
                                padding: "8px 20px",
                                borderRadius: "8px",
                                cursor: "pointer",
                                fontSize: "0.9rem",
                            }}
                        >
                            Clear filters
                        </button>
                    </div>
                ) : (
                    <Row className="g-4">
                        {filteredDoulas.map((doula) => (
                            <Col xs={12} sm={6} md={4} lg={4} key={`${doula.first_name}-${doula.last_name}-${doula.company_name}`}>
                                <Card
                                    className="h-100 shadow-xs practitioner-card hover-card"
                                    onClick={() => handleOpenModal(doula)}
                                    style={{ borderRadius: "12px", border: "1px solid #e8d9c8", cursor: "pointer" }}
                                >
                                    <Card.Body className="d-flex align-items-start gap-3">
                                        <img
                                            src={getAvatarUrl(doula?.img_url)}
                                            alt={`${doula.first_name} ${doula.last_name}`}
                                            style={{
                                                width: "80px",
                                                height: "80px",
                                                objectFit: "cover",
                                                borderRadius: "10px",
                                                flexShrink: 0,
                                            }}
                                        />
                                        <div style={{ minWidth: 0 }}>
                                            <h6 className="mb-1" style={{ color: "#4a3b2f", fontWeight: "600" }}>
                                                {doula.first_name} {doula.last_name}
                                            </h6>
                                            {doula.company_name && (
                                                <p className="mb-1" style={{ fontSize: "0.85rem", color: "#7a6355" }}>
                                                    {doula.company_name}
                                                </p>
                                            )}
                                            <div className="d-flex flex-wrap gap-1 mb-2">
                                                {doula.county && (
                                                    <span style={{ backgroundColor: "#A67B5B", color: "#fff", fontWeight: "500", fontSize: "0.75rem", padding: "3px 8px", borderRadius: "4px" }}>
                                                        {doula.county} County
                                                    </span>
                                                )}
                                                {doula.language && doula.language.split(",").slice(0, 2).map((lang) => (
                                                    <span key={lang.trim()} style={{ backgroundColor: "#A67B5B", color: "#fff", fontWeight: "500", fontSize: "0.75rem", padding: "3px 8px", borderRadius: "4px" }}>
                                                        {lang.trim()}
                                                    </span>
                                                ))}
                                            </div>
                                            <p
                                                className="text-muted mb-0"
                                                style={{
                                                    fontSize: "0.82rem",
                                                    display: "-webkit-box",
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: "vertical",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                }}
                                            >
                                                {doula.pub_description || "No description available."}
                                            </p>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}
            </Container>
        </div>

        {/* Detail Modal */}
        <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
            <Modal.Header closeButton style={{ borderBottom: "1px solid #e8d9c8" }}>
                <Modal.Title style={{ color: "#4a3b2f", fontWeight: "600" }}>
                    {selectedDoula?.first_name} {selectedDoula?.last_name}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {selectedDoula && (
                    <div>
                        <div className="d-flex align-items-center gap-4 mb-4">
                            <img
                                src={getAvatarUrl(selectedDoula.img_url)}
                                alt="Doula"
                                style={{
                                    width: "120px",
                                    height: "120px",
                                    objectFit: "cover",
                                    borderRadius: "12px",
                                    flexShrink: 0,
                                }}
                            />
                            <div>
                                <h5 className="mb-1" style={{ color: "#4a3b2f" }}>{selectedDoula.company_name}</h5>
                                {(selectedDoula.city || selectedDoula.state) && (
                                    <p className="mb-2" style={{ color: "#7a6355", fontSize: "0.9rem" }}>
                                        {[selectedDoula.city, selectedDoula.state].filter(Boolean).join(", ")}
                                    </p>
                                )}
                                <div className="d-flex flex-wrap gap-2">
                                    {selectedDoula.county && (
                                        <span style={{ backgroundColor: "#A67B5B", color: "#fff", fontWeight: "500", fontSize: "0.82rem", padding: "3px 8px", borderRadius: "4px" }}>
                                            {selectedDoula.county} County
                                        </span>
                                    )}
                                    {selectedDoula.language && selectedDoula.language.split(",").map((lang) => (
                                        <span key={lang.trim()} style={{ backgroundColor: "#A67B5B", color: "#fff", fontWeight: "500", fontSize: "0.82rem", padding: "3px 8px", borderRadius: "4px" }}>
                                            {lang.trim()}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <p style={{ color: "#4a3b2f", lineHeight: "1.6" }}>
                            {selectedDoula.pub_description || "No description available."}
                        </p>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer style={{ borderTop: "1px solid #e8d9c8" }}>
                <Button
                    style={{ backgroundColor: "#9b6b47", borderColor: "#9b6b47" }}
                    onClick={handleCloseModal}
                >
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
        </>
    );
};

export default DoulaDirectory;
